import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import CloseIcon from '@mui/icons-material/Close';
import { Box, DialogTitle, IconButton } from '@mui/material';
import { returnUrl, uploadFile } from '../../../aws-exports';
import { useAlert } from '../../../hooks/useAlert';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { editProfile } from '../../../store/actions/actions';
import { capitalizeFirstLetter, convertedArray, convertedArrayReverse } from '../../../utils/methods';
import { DialogActionButtons } from '../../dialogActionButtons/DialogActionButtons';
import { ModalUpload } from '../modalUpload/ModalUpload';
import { Form } from './form/form';
import { BootstrapDialog, dialogStyles, iconButtonStyles, useStyles } from './modalEditProfile.styles';
import { IFormValuesProfile, IPropsModalInviteOrg } from './types';

export const ModalEditProfile = ({ open, setOpen }: IPropsModalInviteOrg): JSX.Element => {
  const { control, register, handleSubmit, formState: { errors }, reset, setValue } = useForm<IFormValuesProfile>();
  const profile = useAppSelector((state) => state.organizerReducer.profile);
  const { setAlert } = useAlert();
  const formRef = useRef<HTMLFormElement>(null);
  const [image, setImage] = useState('');
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [address, setAddress] = useState('');
  const [uploadVisible, setUploadVisible] = useState(false);
  const [imageS3, setImageS3] = useState('');
  const [croppedImage, setCroppedImage] = useState<any>('');
  const [category, setCategory] = useState<string>();
  const [goals, setGoals] = useState<string[]>([]);
  const dispatch = useAppDispatch();
  const classes = useStyles();

  const getUrl = async() => {
    let result = profile.photoUrl;
    if (!profile.photoUrl.startsWith('https://')) {
      result = await returnUrl(profile.photoUrl) as unknown as string;
    }
    setCroppedImage(result);
  };

  useEffect(() => {
    if (profile) {
      getUrl();
      setAddress(profile.address);
      setImageS3(profile.photoUrl);
      setValue('name', profile.name);
      setValue('email', profile.email);
      setValue('address', profile.address);
      setValue('photoUrl', profile.photoUrl);
      setValue('category', profile.category);
      setValue('websiteUrl', profile.websiteUrl);
      setValue('contactName', profile.contactName);
      setValue('phoneNumber', profile.phoneNumber);
      setValue('description', profile.description);
      setCategory(capitalizeFirstLetter(profile.category));
      setValue('sustainableGoals', profile.sustainableGoals);
      setGoals(convertedArrayReverse(profile.sustainableGoals));
    }
  }, [open]);

  const handleClose = (): void => {
    setGoals([]);
    setCategory('');
    setCroppedImage('');
    setSubmitted(false);
    setImage('');
    setImageS3('');
    reset();
    setOpen(false);
  };

  const onSubmit = ():void => {
    setSubmitted(true);
  };

  const savePhoto = (croppedOne: any) => {
    if (croppedOne) {
      uploadFile(croppedOne, setImageS3);
    }
  };

  const editOppProfile = useCallback(async(data: IFormValuesProfile): Promise<void> => {
    try {
      if (data.address.length < 201 && imageS3) {
        await editProfile({
          ...data,
          category: data.category.replace(/\s+/g, '_').toUpperCase(),
          sustainableGoals: convertedArray(data.sustainableGoals),
          photoUrl: imageS3,
        })(dispatch)
          .then(() => {
            setAlert('701', 'success');
          });
        handleClose();
      }
    }
    catch (error) {
      console.log('error', error);
    }
  }, [croppedImage, imageS3]);

  const contentProps = {
    setAddress,
    address,
    setValue,
    setImage,
    croppedImage,
    setCroppedImage,
    errors,
    setUploadVisible,
    register,
    goals,
    category,
    setGoals,
    submitted,
    setCategory,
    required: true,
    control,
  };

  const actionProps = {
    handleClose,
    cancel: 'CANCEL',
    submit: 'SAVE CHANGES',
    handleSubmit: onSubmit,
  };

  const modalProps = {
    image,
    savePhoto,
    setCroppedImage,
    open: uploadVisible,
    setOpen: setUploadVisible,
  };

  return (
    <div>
      <BootstrapDialog
        className={classes.modal}
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
        scroll="body"
        maxWidth="lg"
      >
        <Box
          component="form"
          ref={formRef}
          onSubmit={handleSubmit(editOppProfile)}
        >
          <DialogTitle
            sx={dialogStyles}
            className={classes.modalMainTitle}
          >
            Edit the organisation's details
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={iconButtonStyles}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <Form {...contentProps} />
          <DialogActionButtons {...actionProps} />
        </Box>
      </BootstrapDialog>
      <ModalUpload {...modalProps} />
    </div>
  );
};
